import { useCallback } from 'react'

import { ProfilingRequestSource } from 'clickNgo/configs/profiling/common'
import {
  ContactProccessingStep,
  ContactStatus,
  ContactWorkflowStatus,
  IContact,
  LoadingState,
} from 'clickNgo/entities/contact'
import { updateContact } from 'clickNgo/entities/contact/api'
import { mapContactModifiedValues } from 'clickNgo/entities/contact/api/mapping/mapContactModifiedValues'
import { getJSendErrorMessages } from 'clickNgo/helpers/api'
import { I375Form } from 'clickNgo/models/crud/forms'
import { useGetFormDataField } from 'cng-common/features/form'
import { NotificationController } from 'core-components/library/popup'
import Logger from 'core/utils/Logger'

import { ContactEditorFormKeys } from '../../../configs'
import { ContactEditorSettingFormFields, TContactEditorData, THandlerResult } from '../../../models'

type Params = {
  contact: IContact
  onUpdate: (contact: IContact) => Promise<void> | void
}

export function useContactInactivation({ contact, onUpdate }: Params) {
  const [actualData, updateActualData] = useGetFormDataField<TContactEditorData>(
    ContactEditorFormKeys.settings,
    ContactEditorSettingFormFields.ActualData
  )

  const [, setErrorMessages] = useGetFormDataField<string[]>(
    ContactEditorFormKeys.settings,
    ContactEditorSettingFormFields.ErrorMessages
  )
  const [, setIsLoading] = useGetFormDataField<LoadingState>(
    ContactEditorFormKeys.settings,
    ContactEditorSettingFormFields.IsLoading
  )

  const inactivateContact = useCallback<() => Promise<THandlerResult>>(async () => {
    setIsLoading({ isLoading: true, message: 'Inactivating contact...' })
    setErrorMessages([])

    const request: IContact = {
      ...contact,
      status: ContactStatus.Inactive,
      workflowStatus: ContactWorkflowStatus.New,
      source: ProfilingRequestSource.ClickAndGoPortal,
      modifiedValues: ['status'],
    }

    const response = await updateContact(request, { forceRoles: true })

    if (response) {
      switch (response.status) {
        case 'fail': {
          setIsLoading({ isLoading: false, message: '' })
          setErrorMessages(getJSendErrorMessages(response))
          return {
            hasError: true,
          }
        }
        case 'error': {
          setIsLoading({ isLoading: false, message: '' })
          setErrorMessages(response.message ? [response.message] : [])
          return {
            hasError: true,
          }
        }
        case 'success': {
          const updatedContact: IContact = {
            ...request,
            ...response.data,
            modifiedValues: mapContactModifiedValues(
              response.data.modifiedValues.split(',') as (keyof I375Form)[]
            ),
            modified: new Date().toISOString(),
          }

          await onUpdate(updatedContact)

          updateActualData({
            ...actualData,
            contact: updatedContact,
          })
          break
        }
      }

      setIsLoading({ isLoading: false, message: '' })
      return {
        hasError: false,
      }
    } else {
      setErrorMessages(['Something went wrong!'])
      setIsLoading({ isLoading: false, message: '' })
      return {
        hasError: true,
      }
    }
  }, [actualData, contact, onUpdate, setErrorMessages, setIsLoading, updateActualData])

  const checkAndInactivateContact = useCallback(
    async (steps: ContactProccessingStep[]) => {
      for (let i = 0; i < steps.length; i++) {
        setIsLoading({ isLoading: true, message: steps[i].desc || '' })
        try {
          await new Promise((resolve) => setTimeout(resolve, 1000))
          const result = await steps[i].step()
          if (result === false) {
            Logger.warn('Step returned false, stopping inactivation process')
            return false
          }
        } catch (error) {
          Logger.error('Error during contact inactivation step:', error)
          return false
        } finally {
          setIsLoading({ isLoading: false, message: '' })
        }
      }

      try {
        await inactivateContact()
        NotificationController.showSuccess({
          message: 'Contact inactivated successfully',
        })
        return true
      } catch (error) {
        Logger.error('Error during contact inactivation:', error)
        NotificationController.showError({
          message: 'Failed to inactivate contact',
        })
        return false
      }
    },
    [inactivateContact, setIsLoading]
  )

  return {
    inactivateContact,
    checkAndInactivateContact,
  }
}
